import { Fragment, useCallback, useMemo } from "react"
import { Box, useTheme } from "@mui/material"
import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar"
import { SKELETON_COUNT, SKELETON_INDEX_TO_REF } from "constant"

import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { useDining } from "extensions/iconsiam/hooks/dining"
import { useAppUI } from "providers/ui"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"
import { Head } from "components/Head"
import { DiningIcon } from "components/icons"
import { EmptyData } from "components/EmptyData"
import { VenueSelector } from "components/VenueSelector"
import { MotionDiv, showContent } from "components/motions"
import { DirectorySkeletonCard } from "components/domains/directory"
import { LanguageButton } from "extensions/iconsiam/components/language/LanguageButton"
import { SearchToggleButton } from "components/domains/search"
import { DiningCard } from "extensions/iconsiam/components/domains/dining/DiningCard"
import { HorizontalScrollBarWithButton } from "extensions/iconsiam/components/HorizontalScrollBar/HorizontalScrollBarWithButton"
import { useElementScrollRestoration } from "hooks/scrollbar/useElementScrollRestoration"

const DEFAULT_FILTER_ICON = <DiningIcon />
const ALL_FILTER_ICON = DEFAULT_FILTER_ICON

export const DiningKioskVPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const { openSearch } = useAppUI()
  const {
    restaurants,
    currentCategory,
    categories,
    isMultiVenueProject,
    locationVenueId,
    venues,
    venueFilter,
    hasData,
    hasNextPage,
    endScrollRef,
    filterByCategoryId,
    filterByVenueId,
  } = useDining()

  const { t } = useTranslation({ defaultValue: null })

  const handleChangeCategory = useCallback(
    (e, id) => {
      if (id === null) return
      filterByCategoryId(id)
    },
    [filterByCategoryId]
  )

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.dining) || <DiningIcon />,
    [theme]
  )

  const { styleOverrides = {} } = theme.components?.[`AppDining`] || {}
  const {
    root,
    filterSection,
    filterContainer,
    filterButton,
    gridContainerWrapper,
    gridContainer,
    gridItem,
    diningImageCard,
    emptyDataContainer,
    navigationWrapper,
    venueSelector,
    toolbarContainer,
    actionContainer,
    actionButton,
    searchBarContainer,
  } = styleOverrides

  const { scrollRef } = useElementScrollRestoration<HTMLDivElement>(
    "dining-page-scroll-pane"
  )

  return (
    <>
      <Head title={t("Dining")} />
      <Layout>
        <Box sx={root}>
          {hasData ? (
            <Box sx={gridContainerWrapper} ref={scrollRef}>
              <MotionDiv
                animate="animate"
                initial="initial"
                variants={showContent}
                sx={gridContainer}
              >
                {restaurants.pages.map((page, pageNum) => {
                  return (
                    <Fragment key={pageNum}>
                      {page.data.map((restaurant, index) => {
                        const isAd = restaurant?.properties?.isAd
                        const occupantId = restaurant?.id
                        const isNewShop = restaurant?.properties?.isNewShop
                        const isComingSoon =
                          restaurant?.properties?.isComingSoon
                        return (
                          <DiningCard
                            id={occupantId}
                            data-testid={occupantId}
                            data={restaurant}
                            isNewShop={!isAd && isNewShop}
                            isComingSoon={!isAd && isComingSoon}
                            sx={diningImageCard}
                            key={`${
                              isAd ? "ad-" : "directory-item"
                            }${occupantId}`}
                          />
                        )
                      })}
                    </Fragment>
                  )
                })}
                {hasNextPage &&
                  Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                    <DirectorySkeletonCard
                      ref={
                        index === SKELETON_INDEX_TO_REF ? endScrollRef : null
                      }
                      key={index}
                      sx={gridItem}
                    />
                  ))}
              </MotionDiv>
            </Box>
          ) : (
            <MotionDiv
              animate="animate"
              initial="initial"
              variants={showContent}
              sx={emptyDataContainer}
            >
              <EmptyData
                icon={emptyIcon}
                primary={t("Coming soon")}
                secondary={t("Stay tuned for the update")}
              />
            </MotionDiv>
          )}
          <Box key="app-navigation" sx={navigationWrapper}>
            {categories.length > 0 && (
              <Box sx={filterSection}>
                <HorizontalScrollBarWithButton
                  className="subCategory-filterbar"
                  sx={filterContainer}
                >
                  <CategoryFilterBar
                    sx={filterButton}
                    activeCategory={currentCategory}
                    categories={categories}
                    allButtonIcon={ALL_FILTER_ICON}
                    defaultButtonIcon={DEFAULT_FILTER_ICON}
                    onChange={handleChangeCategory}
                    renderFilterButton={CategoryFilterChip}
                  />
                </HorizontalScrollBarWithButton>
              </Box>
            )}

            <Box sx={toolbarContainer}>
              <Box sx={actionContainer}>
                <LanguageButton sx={actionButton} />
                {additionalMenu}
              </Box>
              <Box sx={searchBarContainer}>
                <SearchToggleButton
                  placeholder={t("Search shops and more...")}
                  onClick={openSearch}
                />
              </Box>
            </Box>
            {isMultiVenueProject && (
              <Box sx={venueSelector}>
                <VenueSelector
                  selectedVenue={venueFilter}
                  venues={venues}
                  onClickVenue={filterByVenueId}
                  currentVenue={locationVenueId}
                />
              </Box>
            )}
            {AppNavigation && <AppNavigation />}
          </Box>
        </Box>
      </Layout>
    </>
  )
}
